package us.laantech.admin.ui

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.descriptors.SerialKind
import kotlinx.serialization.descriptors.StructureKind
import kotlinx.serialization.json.*
import us.laantech.admin.util.*
import us.laantech.common.ui.DropdownMenu
import us.laantech.common.util.generateUUIDv7
import us.laantech.common.util.tryFormatAsDateTime

// Disallows the user from editing these keys, the UI will hide them.
private val DEFAULT_IGNORE_KEYS =
    setOf("posConfigs", "storeExpenses", "departments", "productLicenses", "taxRates", "branches")

// uuid is static by default and does not allow editing,
// these are additional fields that should be displayed but not editable.
private val DEFAULT_STATIC_KEYS = setOf("accountSub")

/**
 * Converts a serial/enum name to a display label.
 *  - "accountSub"        -> "Account Sub"
 *  - "parentCompanyUuid" -> "Parent Company UUID"
 *  - "account_sub"       -> "Account Sub"
 *  - "FIXED_AMOUNT"      -> "FIXED AMOUNT"   (enum constants unchanged)
 */
fun String.toDisplayName(): String {
    if (isEmpty()) return this
    return this
        .replace('_', ' ')
        .replace(Regex("([a-z0-9])([A-Z])"), "$1 $2")    // camelCase hump
        .replace(Regex("([A-Z]+)([A-Z][a-z])"), "$1 $2") // acronym -> Word (UUIDField -> UUID Field)
        .split(" ")
        .filter { it.isNotBlank() }
        .joinToString(" ") { it.replaceFirstChar { c -> c.uppercase() } }
}



/**
 * Renders an editable form for any `@Serializable` class descriptor,
 * driven by a JsonObject map. Calls [onChange] with the new map on every edit.
 */
@Composable
fun SerializableForm(
    descriptor: SerialDescriptor,
    values: Map<String, JsonElement>,
    onChange: (Map<String, JsonElement>) -> Unit,
    modifier: Modifier = Modifier,
    ignoreKeys: Set<String> = DEFAULT_IGNORE_KEYS,
    staticKeys: Set<String> = DEFAULT_STATIC_KEYS,
) {
    Column(modifier) {
        for (field in formFieldsOf(descriptor)) {
            if (field.name in ignoreKeys) continue
            val current = values[field.name] ?: JsonNull
            FieldEditor(
                field = field,
                value = current,
                onValueChange = { newVal ->
                    onChange(values.toMutableMap().also { it[field.name] = newVal })
                },
                ignoreKeys = ignoreKeys,
                staticKeys = staticKeys,
            )
        }
    }
}

@Composable
private fun FieldEditor(
    field: FormField,
    value: JsonElement,
    onValueChange: (JsonElement) -> Unit,
    ignoreKeys: Set<String> = emptySet(),
    staticKeys: Set<String> = emptySet(),
) {
    // Static fields (uuids and configured keys) are displayed but not editable.
    if (isUuidFieldName(field.name) || field.name in staticKeys) {
        Column(Modifier.padding(4.dp)) {
            Text(
                text = field.name.toDisplayName(),
                style = MaterialTheme.typography.labelSmall,
            )
            Text(text = value.asStringOrEmpty())
        }
        return
    }

    when (field.kind) {
        PrimitiveKind.STRING -> OutlinedTextField(
            value = value.asStringOrEmpty(),
            onValueChange = { onValueChange(it.toJsonStringElement(field.isNullable)) },
            label = { Text(field.name.toDisplayName()) },
            modifier = Modifier.padding(4.dp),
        )

        PrimitiveKind.LONG -> LongFieldEditor(
            label = field.name,
            value = value,
            isNullable = field.isNullable,
            onValueChange = onValueChange,
        )

        PrimitiveKind.INT, PrimitiveKind.SHORT, PrimitiveKind.BYTE,
        PrimitiveKind.FLOAT, PrimitiveKind.DOUBLE -> OutlinedTextField(
            value = value.asStringOrEmpty(),
            onValueChange = { onValueChange(it.toJsonNumberElement(field.isNullable)) },
            label = { Text("${field.name.toDisplayName()} (number)") },
            modifier = Modifier.padding(4.dp),
        )

        PrimitiveKind.BOOLEAN -> {
            val checked = (value as? JsonPrimitive)?.booleanOrNull == true
            Column(Modifier.padding(4.dp)) {
                Text(field.name.toDisplayName())
                Checkbox(checked = checked, onCheckedChange = { onValueChange(it.toJsonElement()) })
            }
        }

        StructureKind.CLASS -> {
            // Recurse into the nested object.
            Text(field.name.toDisplayName())
            val nested = (value as? JsonObject)?.toMap()
                ?: emptyJsonObjectFor(field.descriptor).toMap()
            SerializableForm(
                descriptor = field.descriptor,
                values = nested,
                onChange = { onValueChange(JsonObject(it)) },
                modifier = Modifier.padding(start = 16.dp),
                ignoreKeys = ignoreKeys,
                staticKeys = staticKeys,
            )
        }

        StructureKind.MAP -> {
            println("Unsupported field: ${field.name} (${field.kind})")

            // Simple JSON-text fallback for collections.
            OutlinedTextField(
                value = value.toString(),
                onValueChange = {
                    runCatching { FormJson.parseToJsonElement(it) }
                        .onSuccess(onValueChange)
                },
                label = { Text("${field.name} (JSON)") },
                modifier = Modifier.padding(4.dp),
            )
        }

        StructureKind.LIST -> ListEditor(
            field = field,
            value = value,
            onValueChange = onValueChange,
            ignoreKeys = ignoreKeys,
            staticKeys = staticKeys,
        )

        SerialKind.ENUM -> {
            val entries = (0 until field.descriptor.elementsCount)
                .map { field.descriptor.getElementName(it) }
            val selected = (value as? JsonPrimitive)?.contentOrNull
            DropdownMenu(
                label = field.name.toDisplayName(),
                items = entries,
                selectedItem = selected,
                itemLabel = { it.toDisplayName() },
                onItemSelected = { onValueChange(if (it != null) JsonPrimitive(it) else JsonNull) },
                modifier = Modifier.padding(4.dp),
                noneLabel = if (field.isNullable) "None" else null,
            )
        }

        else -> Text("Unsupported field: ${field.name} (${field.kind})")
    }
}


/**
 * Editor for `List<T>`. Looks at the item descriptor and either:
 *  - renders a nested `SerializableForm` per item (if T is a class), or
 *  - renders a single primitive field per item.
 * Provides Add / Remove buttons.
 */
@Composable
private fun ListEditor(
    field: FormField,
    value: JsonElement,
    onValueChange: (JsonElement) -> Unit,
    ignoreKeys: Set<String>,
    staticKeys: Set<String>,
) {
    val itemDescriptor: SerialDescriptor = field.descriptor.getElementDescriptor(0)
    val items: List<JsonElement> = (value as? JsonArray)?.toList() ?: emptyList()

    fun update(newItems: List<JsonElement>) = onValueChange(JsonArray(newItems))

    Column(Modifier.padding(vertical = 4.dp).fillMaxWidth()) {
        HorizontalDivider()
        Text("${field.name.toDisplayName()} (${items.size})")

        items.forEachIndexed { index, item ->
            Column(Modifier.padding(start = 16.dp, top = 8.dp)) {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Text("${itemDescriptor.serialName.substringAfterLast('.')} #${index + 1}")
                    OutlinedButton(onClick = {
                        update(items.toMutableList().also { it.removeAt(index) })
                    }) { Text("Remove") }
                }

                when (itemDescriptor.kind) {
                    StructureKind.CLASS -> {
                        val nested = (item as? JsonObject)?.toMap()
                            ?: emptyJsonObjectFor(itemDescriptor).toMap()
                        SerializableForm(
                            descriptor = itemDescriptor,
                            values = nested,
                            onChange = { newMap ->
                                update(items.toMutableList().also { it[index] = JsonObject(newMap) })
                            },
                            modifier = Modifier.padding(start = 8.dp),
                            ignoreKeys = ignoreKeys,
                            staticKeys = staticKeys,
                            )
                    }

                    PrimitiveKind.STRING -> OutlinedTextField(
                        value = item.asStringOrEmpty(),
                        onValueChange = { newText ->
                            update(items.toMutableList().also {
                                it[index] = newText.toJsonStringElement(itemDescriptor.isNullable)
                            })
                        },
                        label = { Text("${field.name.toDisplayName()} #${index + 1}") },
                        modifier = Modifier.padding(4.dp),
                    )

                    PrimitiveKind.LONG -> LongFieldEditor(
                        label = "${field.name.toDisplayName()} #${index + 1}",
                        value = item,
                        isNullable = itemDescriptor.isNullable,
                        onValueChange = { newVal ->
                            update(items.toMutableList().also { it[index] = newVal })
                        },
                    )

                    PrimitiveKind.INT, PrimitiveKind.SHORT, PrimitiveKind.BYTE,
                    PrimitiveKind.FLOAT, PrimitiveKind.DOUBLE -> OutlinedTextField(
                        value = item.asStringOrEmpty(),
                        onValueChange = { newText ->
                            update(items.toMutableList().also {
                                it[index] = newText.toJsonNumberElement(itemDescriptor.isNullable)
                            })
                        },
                        label = { Text("${field.name.toDisplayName()} #${index + 1} (number)") },
                        modifier = Modifier.padding(4.dp),
                    )

                    SerialKind.ENUM -> {
                        val entries = (0 until itemDescriptor.elementsCount)
                            .map { itemDescriptor.getElementName(it) }
                        val selected = (item as? JsonPrimitive)?.contentOrNull
                        DropdownMenu(
                            label = "${field.name.toDisplayName()} #${index + 1}",
                            items = entries,
                            selectedItem = selected,
                            itemLabel = { it.toDisplayName() },
                            onItemSelected = { choice ->
                                update(items.toMutableList().also {
                                    it[index] = if (choice != null) JsonPrimitive(choice) else JsonNull
                                })
                            },
                            modifier = Modifier.padding(4.dp),
                        )
                    }

                    else -> Text("Unsupported list item kind: ${itemDescriptor.kind}")
                }
            }
        }

        Button(
            onClick = {
                val newItem: JsonElement = when (itemDescriptor.kind) {
                    StructureKind.CLASS -> fillBlankUuids(itemDescriptor, emptyJsonObjectFor(itemDescriptor))
                    PrimitiveKind.STRING -> JsonPrimitive("")
                    PrimitiveKind.BOOLEAN -> JsonPrimitive(false)
                    PrimitiveKind.INT, PrimitiveKind.LONG, PrimitiveKind.SHORT, PrimitiveKind.BYTE -> JsonPrimitive(0)
                    PrimitiveKind.FLOAT, PrimitiveKind.DOUBLE -> JsonPrimitive(0.0)
                    SerialKind.ENUM -> JsonPrimitive(itemDescriptor.getElementName(0))
                    else -> JsonNull
                }
                update(items + newItem)
            },
            modifier = Modifier.padding(top = 8.dp),
        ) { Text("Add ${itemDescriptor.serialName.substringAfterLast('.')}") }
    }
}

// Auto generate UUIDs for missing / blank strings

/**
 * Returns true if the field name (case-insensitive) contains "uuid".
 * Matches "branch_uuid", "branchUuid", "uuid", etc.
 */
fun isUuidFieldName(name: String): Boolean =
    name.contains("uuid", ignoreCase = true)

/**
 * Returns true when the JsonElement represents a missing / blank string value.
 */
fun JsonElement?.isBlankOrNull(): Boolean = when (this) {
    null, JsonNull -> true
    is JsonPrimitive -> isString && content.isBlank() || (!isString && content.isBlank())
    else -> false
}

/**
 * Walks the descriptor tree and assigns a fresh UUIDv7 to every string field
 * whose name contains "uuid" and whose current value is blank/null.
 * Recurses into nested classes and lists.
 */
fun fillBlankUuids(descriptor: SerialDescriptor, value: JsonElement): JsonElement {
    return when (descriptor.kind) {
        StructureKind.CLASS -> {
            val obj = (value as? JsonObject) ?: emptyJsonObjectFor(descriptor)
            val newMap = obj.toMutableMap()
            for (i in 0 until descriptor.elementsCount) {
                val name = descriptor.getElementName(i)
                val child = descriptor.getElementDescriptor(i)
                val current = newMap[name] ?: JsonNull

                newMap[name] = when {
                    child.kind == PrimitiveKind.STRING
                            && isUuidFieldName(name)
                            && current.isBlankOrNull() ->
                        JsonPrimitive(generateUUIDv7())

                    else -> fillBlankUuids(child, current)
                }
            }
            JsonObject(newMap)
        }

        StructureKind.LIST -> {
            val arr = (value as? JsonArray) ?: return value
            val itemDescriptor = descriptor.getElementDescriptor(0)
            JsonArray(arr.map { fillBlankUuids(itemDescriptor, it) })
        }

        else -> value
    }
}


// Primitive.LONG editor:
/**
 * Editor for `Long` fields. Renders a numeric text input plus a 📅 button
 * that opens [DateTimeEntryModal] so the user can pick an epoch-millis
 * date/time instead of typing one.
 *
 * Works for both nullable and non-nullable longs, and for fields that
 * happen to be arbitrary numbers (you just don't have to use the button).
 */
@Composable
private fun LongFieldEditor(
    label: String,
    value: JsonElement,
    isNullable: Boolean,
    onValueChange: (JsonElement) -> Unit,
) {
    var showPicker by remember { mutableStateOf(false) }
    val currentMillis: Long? = (value as? JsonPrimitive)?.longOrNull

    Row(
        modifier = Modifier.padding(4.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        OutlinedTextField(
            value = value.asStringOrEmpty(),
            onValueChange = { onValueChange(it.toJsonNumberElement(isNullable)) },
            label = { Text("${label.toDisplayName()} (long / epoch ms)") },
            supportingText = {
                val preview = currentMillis?.let { tryFormatAsDateTime(it) }
                if (preview != null) Text(preview)
            },
            modifier = Modifier.weight(1f),
        )
        TextButton(
            onClick = { showPicker = true },
            modifier = Modifier.padding(start = 8.dp),
        ) { Text("DateTime") }
    }

    if (showPicker) {
        DateTimeEntryModal(
            initialEpochMillis = currentMillis,
            title = "Select ${label.toDisplayName()}",
            onDismiss = { showPicker = false },
            onConfirm = { epochMillis ->
                onValueChange(JsonPrimitive(epochMillis))
                showPicker = false
            },
        )
    }
}



